import Component from '@glimmer/component';
import { service } from '@ember/service';
import { LinkTo } from '@ember/routing';
import { t } from 'ember-intl';
import { PhArrowLeft } from 'ember-phosphor-icons';
import Chip from 'tio-reimagined/components/chip';
import TioAppearance from 'tio-common/components/tio/appearance';
import ProgramApprovalPage from 'tio-common/components/tas/program-approval/program-approval-page';
import ENV from 'tio-employee/config/environment';
import type TASProgramInstanceModel from 'tio-data/models/tas-program-instance';
import type { ISession } from 'tio-common/types/services';

interface RouteSignature {
  Args: {
    model: TASProgramInstanceModel;
  };
}

export default class TasProgramsRoute extends Component<RouteSignature> {
  @service declare session: ISession;

  <template>
    <ProgramApprovalPage @model={{@model}} @consumingApp="tio-employee" class="max-w-7xl">
      <:header>
        <LinkTo
          @route="authenticated.admin.tas.approval-queue"
          class="inline-flex items-center gap-1 text-xs text-midnight dark:text-gray-300 underline underline-offset-3 mb-3"
        >
          <PhArrowLeft @size={{16}} class="leading-none" />
          {{t "tuition_assistance.approval_queue.title"}}
        </LinkTo>
        <div class="flex justify-between items-start">
          <div class="flex flex-col gap-2">
            {{! Program Name and Status Chips }}
            <div class="flex items-center gap-2">
              <h1 class="truncate max-w-md">
                {{@model.programName}}
              </h1>
              <Chip @intent={{@model.badgeIntent}} @size="sm">
                {{@model.badgeLabel}}
              </Chip>
              {{#if @model.isTrial}}
                <Chip @intent="danger" @size="sm">
                  {{t "common.trial"}}
                </Chip>
              {{/if}}
            </div>
            {{! Program Instance ID }}
            <div class="text-base font-semibold text-gray-700">
              {{! template-lint-disable no-bare-strings }}
              Program #{{@model.id}}
            </div>
            {{! Employee Name and Payroll ID }}
            <div class="flex items-center gap-1.5 text-sm text-gray-500">
              <LinkTo
                @route="authenticated.admin.employees.show"
                @model={{@model.employee.id}}
                class="font-semibold cursor-pointer truncate max-w-xs hover:underline"
              >
                {{@model.employee.person.fullName}}
              </LinkTo>
              <div class="w-1 h-1 bg-gray-400 dark:bg-gray-600 rounded-full shrink-0"></div>
              <LinkTo
                @route="authenticated.admin.employees.show"
                @model={{@model.employee.id}}
                class="font-semibold text-gray-900 shrink truncate max-w-40 cursor-pointer hover:underline"
                title={{@model.employee.payrollId}}
              >
                {{t "tuition_assistance.applications.payroll_id"}}
                {{@model.employee.payrollId}}
              </LinkTo>
              <div class="w-1 h-1 bg-gray-400 dark:bg-gray-600 rounded-full shrink-0"></div>
              <span class="font-semibold truncate max-w-xs">
                {{@model.employee.company.name}}
              </span>
            </div>
          </div>
          <div class="flex flex-col items-end gap-2">
            {{#if @model.id}}
              <LinkTo
                @route="authenticated.admin.tuition-assistance.program-instances.show"
                @model={{@model.id}}
                class="tio-btn tio-btn-default tio-btn-outlined"
              >
                {{t "common.tas_approval_queue.view"}}
              </LinkTo>
            {{/if}}
            {{#if @model.id}}
              <TioAppearance
                @apiKey={{ENV.apiKey}}
                @apiHost={{ENV.apiHost}}
                @token={{this.session.data.authenticated.access_token}}
                @type="tas_program_instance"
                @documentId={{@model.id}}
              />
            {{/if}}
          </div>
        </div>
      </:header>
    </ProgramApprovalPage>
  </template>
}
